import type {
  IntegrationActivityEntry,
  IntegrationAuthMode,
  IntegrationConnectionScope,
  IntegrationDraft,
  IntegrationDraftAuth,
  IntegrationDraftBinding,
  IntegrationDraftCopy,
  IntegrationDraftCredentialField,
  IntegrationDraftGeneration,
  IntegrationDraftOperation,
  IntegrationDraftRate,
  IntegrationDraftStatus,
  IntegrationDraftUpdate,
  IntegrationLocale,
} from '@/api/adminClient';
import { formatAge } from '@/lib/microvm';
import type { StatusTone } from '@/lib/runTelemetry';

export const BUILDER_STEPS = [
  { id: 'contract', label: 'API contract', hint: 'Import an OpenAPI spec' },
  { id: 'identity', label: 'Identity & brand', hint: 'Capability, category, logo' },
  { id: 'pricing', label: 'Pricing', hint: 'Optional — free or per call' },
  { id: 'copy', label: 'Copy & languages', hint: 'Words, translations, guidance' },
  { id: 'credentials', label: 'Connection', hint: 'Whose account, auth, secrets' },
  { id: 'review', label: 'Review & submit', hint: 'Checklist and manifest' },
] as const;

export type BuilderStepId = (typeof BUILDER_STEPS)[number]['id'];

/** Checklist items that belong to each step, so the rail can show which step still needs work. */
const STEP_CHECKS: Record<BuilderStepId, string[]> = {
  contract: ['contract', 'base-url', 'operations'],
  identity: ['capability', 'logo'],
  copy: ['copy-en', 'translations'],
  pricing: ['pricing-labels', 'pricing-links'],
  credentials: ['connection', 'auth', 'secrets'],
  review: [],
};

export function stepComplete(draft: IntegrationDraft, step: BuilderStepId): boolean {
  const ids = STEP_CHECKS[step];
  if (step === 'review') return draft.status === 'approved' || draft.status === 'published';
  return ids.every((id) => draft.checklist.find((check) => check.id === id)?.ok ?? false);
}

export const LOCALES: Array<{ id: IntegrationLocale; label: string; native: string }> = [
  { id: 'en', label: 'English', native: 'English' },
  { id: 'es', label: 'Spanish', native: 'Español' },
  { id: 'cs', label: 'Czech', native: 'Čeština' },
  { id: 'ja', label: 'Japanese', native: '日本語' },
];

export const CATEGORIES = ['Maps', 'AI', 'Messaging', 'Productivity', 'Weather', 'Realtime', 'Data', 'Public Safety'];

export const STATUS_META: Record<IntegrationDraftStatus, { label: string; tone: StatusTone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  in_review: { label: 'In review', tone: 'active' },
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Changes requested', tone: 'warning' },
  published: { label: 'Live', tone: 'success' },
};

export const DRAFT_LIST_PAGE_SIZE = 25;

export type DraftListFilter = IntegrationDraftStatus | 'all';

/** What the drafts list shows, as its link keeps it: `?q=&status=&page=` (page counts from 1). */
export type DraftListView = { q: string; status: DraftListFilter; page: number };

const DRAFT_LIST_FILTERS: DraftListFilter[] = ['all', 'draft', 'in_review', 'approved', 'rejected', 'published'];

export function readDraftListView(params: URLSearchParams): DraftListView {
  const status = params.get('status') as DraftListFilter | null;
  const page = Number(params.get('page'));
  return {
    q: params.get('q') ?? '',
    status: status && DRAFT_LIST_FILTERS.includes(status) ? status : 'all',
    page: Number.isInteger(page) && page > 1 ? page : 1,
  };
}

/** The query for a view, leaving out what is already the default. */
export function draftListSearchParams(view: DraftListView): URLSearchParams {
  const params = new URLSearchParams();
  if (view.q.trim()) params.set('q', view.q.trim());
  if (view.status !== 'all') params.set('status', view.status);
  if (view.page > 1) params.set('page', String(view.page));
  return params;
}

/** The filter options in the order they read, with counts once they are known. */
export function draftListFilterOptions(counts: Record<IntegrationDraftStatus, number> | undefined) {
  const total = counts ? Object.values(counts).reduce((sum, count) => sum + count, 0) : undefined;
  return DRAFT_LIST_FILTERS.map((id) => ({
    id,
    label: id === 'all' ? 'All' : STATUS_META[id].label,
    count: id === 'all' ? total : counts?.[id],
  }));
}

/** What an empty page says: nothing matches the search or the status, or nothing has been made yet. */
export function draftListEmptyMessage(view: Pick<DraftListView, 'q' | 'status'>): string | undefined {
  const q = view.q.trim();
  const status = view.status === 'all' ? undefined : STATUS_META[view.status].label;
  if (q && status) return `No integrations match “${q}” with the status ${status}.`;
  if (q) return `No integrations match “${q}”.`;
  if (status) return `No integrations have the status ${status}.`;
  return undefined;
}

const DRAFT_CHANGES_META: Record<IntegrationDraftStatus, { label: string; tone: StatusTone }> = {
  draft: { label: 'Live · draft changes', tone: 'neutral' },
  in_review: { label: 'Live · changes in review', tone: 'active' },
  approved: { label: 'Live · changes approved', tone: 'success' },
  rejected: { label: 'Live · changes need work', tone: 'warning' },
  published: { label: 'Live · changes approved', tone: 'success' },
};

/**
 * Published but not released yet: only its testing organizations see this version.
 * With a working draft on a live integration, the status is the draft's.
 */
export function statusMetaFor(
  draft: Pick<IntegrationDraft, 'status' | 'audience'> & Partial<Pick<IntegrationDraft, 'hasDraftChanges' | 'hasRelease' | 'testing'>>,
): { label: string; tone: StatusTone } {
  if (draft.hasDraftChanges) return DRAFT_CHANGES_META[draft.status];
  if (draft.status === 'published' && draft.audience === 'internal') return { label: 'Testing', tone: 'active' };
  if (draft.testing && !draft.hasRelease) return { label: 'Testing · editing', tone: 'active' };
  return STATUS_META[draft.status];
}

export type LiveEditAction = 'edit' | 'withdraw' | 'publish_testing' | 'stop_testing' | 'submit' | 'approve' | 'reject' | 'apply' | 'discard';

/**
 * What can be done to a live integration and its working draft, in the order
 * the buttons read. Approving, rejecting and applying are for integration
 * approvers; the rest is for anyone who can build integrations.
 */
export function liveEditActions(
  draft: Pick<IntegrationDraft, 'hasRelease' | 'hasDraftChanges' | 'status' | 'testing'>,
  canApprove: boolean,
): LiveEditAction[] {
  if (!draft.hasRelease) return [];
  if (!draft.hasDraftChanges) return ['edit'];
  const testing: LiveEditAction = draft.testing ? 'stop_testing' : 'publish_testing';
  if (draft.status === 'draft' || draft.status === 'rejected') return [testing, 'submit', 'discard'];
  if (draft.status === 'in_review') return [...(canApprove ? (['approve', 'reject'] as const) : []), testing, 'withdraw', 'discard'];
  return [...(canApprove ? (['apply'] as const) : []), testing, 'withdraw', 'discard'];
}

export const LIVE_EDIT_LABELS: Record<LiveEditAction, string> = {
  edit: 'Edit',
  withdraw: 'Back to editing',
  publish_testing: 'Publish for testing',
  stop_testing: 'Stop testing',
  submit: 'Submit for review',
  approve: 'Approve',
  reject: 'Reject',
  apply: 'Apply changes',
  discard: 'Discard draft',
};

const SECTION_NAMES: Record<string, string> = {
  contract: 'API contract',
  identity: 'identity',
  pricing: 'pricing',
  copy: 'copy',
  connection: 'connection',
};

const ACTIVITY_SENTENCES: Record<IntegrationActivityEntry['action'], string> = {
  created: 'created the integration',
  draft_started: 'started draft changes to the live version',
  edited: 'edited the draft',
  submitted: 'submitted it for review',
  withdrawn: 'took it back to editing',
  approved: 'approved the changes',
  rejected: 'rejected the changes',
  published_for_testing: 'published it for its testing organizations',
  testing_stopped: 'stopped testing',
  tested: 'tested an operation',
  published: 'published it',
  applied: 'applied the changes for whoever can use it',
  released: 'released it to whoever can use it',
  discarded: 'discarded the draft changes',
  retired: 'unpublished it for everyone',
  deleted: 'deleted it',
};

/** "edited pricing and copy", "approved the changes". */
export function activitySentence(entry: Pick<IntegrationActivityEntry, 'action' | 'sections'>): string {
  if (entry.action === 'edited' && entry.sections.length) {
    const names = entry.sections.map((section) => SECTION_NAMES[section] ?? section);
    return `edited ${names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0]}`;
  }
  return ACTIVITY_SENTENCES[entry.action] ?? entry.action;
}

export function activityActor(entry: Pick<IntegrationActivityEntry, 'actor' | 'actorName'>): string {
  return entry.actorName ? `${entry.actorName} (${entry.actor})` : entry.actor;
}

export function isEditable(status: IntegrationDraftStatus): boolean {
  return status === 'draft' || status === 'rejected';
}

export function emptyCopy(): IntegrationDraftCopy {
  return { title: '', headline: '', description: '', longDescription: '', capabilities: [], agentGuidance: '', rateLabels: {} };
}

export function copyFor(draft: IntegrationDraft, locale: IntegrationLocale): IntegrationDraftCopy {
  return { ...emptyCopy(), ...(draft.copy[locale] ?? {}) };
}

/**
 * Capabilities with a typed interface in integrations-core; a connector may not
 * claim one. Mirrors the server's CORE_CAPABILITIES, which is authoritative.
 */
export const CORE_CAPABILITIES = ['messaging.sms', 'email.send', 'email.read', 'calendar.events', 'files', 'ai.generate', 'ai.chat', 'collaboration.chat'];

/** Instant feedback for the capability field; the server applies the same rules and has the last word. */
export function capabilityProblem(value: string): string | undefined {
  if (!value) return 'Name the capability apps will require.';
  if (value.length > 48) return 'Keep it under 48 characters.';
  if (!value.includes('.')) return 'Use at least two dot-separated parts, like directory.lookup.';
  if (!/^[a-z][a-z0-9]*(?:\.[a-z][a-z0-9]*){1,2}$/.test(value)) {
    return 'Two or three parts of lowercase letters and digits, each starting with a letter.';
  }
  if (CORE_CAPABILITIES.includes(value)) return `${value} is a core capability. Build it as an SDK adapter instead.`;
  return undefined;
}

/** Keep typing inside the capability alphabet: lowercase letters, digits and dots. */
export function sanitizeCapabilityInput(value: string): string {
  return value.toLowerCase().replace(/[\s_-]+/g, '.').replace(/[^a-z0-9.]/g, '').replace(/\.{2,}/g, '.');
}

/** Same rules as the server: 3–60 characters of letters, digits, spaces and a little punctuation, deduplicated. */
export function normalizeKeyword(value: string): string | undefined {
  const keyword = value.trim().replace(/\s+/g, ' ').toLowerCase();
  if (keyword.length < 3 || keyword.length > 60 || !/^[\p{L}\p{N}][\p{L}\p{N} .&'/-]*$/u.test(keyword)) return undefined;
  return keyword;
}

/** Whether a prompt would auto-pin: whole-word, case-insensitive, literal. */
export function promptMatchesKeywords(prompt: string, keywords: string[]): boolean {
  const text = prompt.toLowerCase();
  return keywords.some((keyword) => {
    const phrase = keyword.trim().toLowerCase();
    if (phrase.length < 3) return false;
    const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|[^\\p{L}\\p{N}])${escaped}($|[^\\p{L}\\p{N}])`, 'u').test(text);
  });
}

/** Kebab-case id from a title: "Acme Directory API" → "acme-directory-api". */
export function slugifyIntegrationId(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[^a-z]+/, '')
    .slice(0, 48)
    .replace(/-$/, '');
}

export function rateIdFromLabel(label: string, taken: string[]): string {
  const base = slugifyIntegrationId(label) || 'rate';
  let id = base;
  for (let n = 2; taken.includes(id); n += 1) id = `${base}-${n}`;
  return id;
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 });

export function formatRate(rate: IntegrationDraftRate): string {
  if (rate.billingModel === 'included') return 'Included';
  const price = `${USD.format(rate.providerRate)} ${rate.unit}`.trim();
  return rate.freeTier ? `${price} · first ${rate.freeTier.toLocaleString('en-US')} free/month` : price;
}

/** What one call costs before markup: providerRate spread over the units it buys. */
export function perCallCost(rate: Pick<IntegrationDraftRate, 'providerRate' | 'unitsPerPrice' | 'billingModel'>): string {
  if (rate.billingModel === 'included' || rate.providerRate === 0) return '$0';
  const value = rate.providerRate / (rate.unitsPerPrice || 1);
  return `$${value >= 0.01 ? value.toFixed(4).replace(/0+$/, '').replace(/\.$/, '') : String(Number(value.toPrecision(3)))}`;
}

export const MAX_LOGO_BYTES = 180 * 1024;

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the file'));
    reader.readAsDataURL(file);
  });
}

/** "just now" for the first minute, then the admin's usual compact age. */
export function relativeTime(iso: string, now: number = Date.now()): string {
  const at = Date.parse(iso);
  if (!Number.isFinite(at) || now - at < 60_000) return 'just now';
  return `${formatAge(iso, now)} ago`;
}

/** The Builder stores the data description for agents and never reads it; the one rule here matches the API's size cap. */
export const MAX_DATA_DESCRIPTION_BYTES = 64 * 1024;

/**
 * The text typed into the data description editor, read as what the API takes: a JSON object, or null for blank.
 * A value that is not an object (an array, a string, a number) is refused here, as the API would refuse it.
 */
export function parseDataDescriptionInput(text: string): { value: Record<string, unknown> | null } | { error: string } {
  const trimmed = text.trim();
  if (!trimmed) return { value: null };
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (error) {
    return { error: `Not valid JSON: ${error instanceof Error ? error.message.replace(/^JSON\.parse: /, '') : 'cannot parse it'}` };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return { error: 'The data description must be a JSON object: { "version": 1, ... }' };
  const bytes = new TextEncoder().encode(JSON.stringify(parsed)).length;
  if (bytes > MAX_DATA_DESCRIPTION_BYTES) return { error: `The data description must be under ${MAX_DATA_DESCRIPTION_BYTES / 1024} KB (this one is ${Math.ceil(bytes / 1024)} KB)` };
  const pollable = pollableProblem(parsed as Record<string, unknown>) ?? sensitiveProblem(parsed as Record<string, unknown>);
  if (pollable) return { error: pollable };
  return { value: parsed as Record<string, unknown> };
}

/** The data description's operation entries that are objects. */
function describedOperations(value: Record<string, unknown>): Array<Record<string, unknown>> {
  return Array.isArray(value.operations) ? value.operations.filter((entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object' && !Array.isArray(entry)) : [];
}

/**
 * The one part Studio reads itself: an operation's `pollable` (where a Workflow poll trigger finds new items).
 * The API refuses an empty items path or id field (`dataDescriptionPollableProblem`); the editor says so first.
 */
function pollableProblem(value: Record<string, unknown>): string | undefined {
  for (const operation of describedOperations(value)) {
    if (!('pollable' in operation)) continue;
    const pollable = operation.pollable as Record<string, unknown> | null;
    const name = typeof operation.operationId === 'string' ? `“${operation.operationId}”` : 'without an operationId';
    if (!pollable || typeof pollable !== 'object' || Array.isArray(pollable)) return `Operation ${name}: pollable must be an object: { "itemsPath": ..., "idField": ... }`;
    for (const key of ['itemsPath', 'idField']) {
      const field = pollable[key];
      if (typeof field !== 'string' || !field.trim()) return `Operation ${name}: pollable.${key} must be a non-empty string`;
    }
  }
  return undefined;
}

/**
 * An operation's `sensitive` (its output is personal, e.g. a transcript: Workflows then keep none of it
 * on run pages) must be true or false; the API refuses anything else (`dataDescriptionPollableProblem`).
 */
function sensitiveProblem(value: Record<string, unknown>): string | undefined {
  for (const operation of describedOperations(value)) {
    if (!('sensitive' in operation) || typeof operation.sensitive === 'boolean') continue;
    const name = typeof operation.operationId === 'string' ? `“${operation.operationId}”` : 'without an operationId';
    return `Operation ${name}: sensitive must be true or false`;
  }
  return undefined;
}

/** The saved data description as editor text: pretty-printed, or empty when there is none. */
export function dataDescriptionText(value: Record<string, unknown> | undefined): string {
  return value ? JSON.stringify(value, null, 2) : '';
}

/** One line on what is stored: how many operations it describes and which claim types it turns on. */
export function describeDataDescription(value: Record<string, unknown> | undefined): string {
  if (!value) return 'Not set. Agents get the operations and guidance only; no tools are built from them.';
  const operations = Array.isArray(value.operations) ? value.operations.length : 0;
  const claims = Array.isArray(value.claims) ? value.claims.filter((claim): claim is string => typeof claim === 'string') : [];
  const parts = [`${operations} operation${operations === 1 ? '' : 's'} described`];
  if (claims.length) parts.push(`claims: ${claims.join(', ')}`);
  const pollable = describedOperations(value).filter((operation) => operation.pollable && typeof operation.operationId === 'string').map((operation) => operation.operationId as string);
  if (pollable.length) parts.push(`pollable: ${pollable.join(', ')}`);
  const sensitive = describedOperations(value).filter((operation) => operation.sensitive === true && typeof operation.operationId === 'string').map((operation) => operation.operationId as string);
  if (sensitive.length) parts.push(`sensitive: ${sensitive.join(', ')}`);
  if (typeof value.sensitivity === 'string') parts.push(value.sensitivity);
  return parts.join(' · ');
}

/** How a binding reads in the Builder: `body.filter.content.coveringRealmIds ← jurisdiction`, or `= null` for a fixed value. */
export function bindingLabel(binding: IntegrationDraftBinding): string {
  const target = [binding.in, ...binding.path].join('.');
  return binding.setting !== undefined ? `${target} ← org setting ${binding.setting}` : `${target} = ${JSON.stringify(binding.value)}`;
}

/** The distinct org settings an operation binds, in order. */
export function bindingSettings(operation: Pick<IntegrationDraftOperation, 'bindings'> | undefined): string[] {
  return [...new Set((operation?.bindings ?? []).flatMap((binding) => (binding.setting !== undefined ? [binding.setting] : [])))];
}

/** The bindings editor's text as stored: empty for none. */
export function bindingsText(bindings: IntegrationDraftBinding[] | undefined): string {
  return bindings?.length ? JSON.stringify(bindings, null, 2) : '';
}

/**
 * The bindings editor's text, parsed for saving. Only the shape is checked here;
 * the server applies the full rules and names what is wrong.
 */
export function parseBindingsInput(text: string): { value: IntegrationDraftBinding[] } | { error: string } {
  if (!text.trim()) return { value: [] };
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { error: 'The bindings are not valid JSON.' };
  }
  if (!Array.isArray(parsed)) return { error: 'The bindings are a JSON list.' };
  for (const item of parsed) {
    const binding = item as Partial<IntegrationDraftBinding> | null;
    if (!binding || typeof binding !== 'object' || !['path', 'query', 'body'].includes(binding.in as string) || !Array.isArray(binding.path)) {
      return { error: 'Each binding has "in" (path, query or body) and "path" (a list of names).' };
    }
    if ((binding.setting === undefined) === !('value' in binding)) return { error: 'Each binding has either "setting" or "value".' };
  }
  return { value: parsed as IntegrationDraftBinding[] };
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong';
}

export type StepProps = {
  draft: IntegrationDraft;
  readOnly: boolean;
  saving: boolean;
  save: (update: IntegrationDraftUpdate) => Promise<IntegrationDraft | undefined>;
  replace: (draft: IntegrationDraft) => void;
  next: () => void;
};

/** OAuth where a person signs in (authorization code). Client credentials are server to server, like an API key. */
export function authSignsIn(auth: Pick<IntegrationDraftAuth, 'kind' | 'grant'>): boolean {
  return auth.kind === 'oauth2' && auth.grant !== 'client_credentials';
}

/**
 * Static credentials organizations or people enter themselves: a connector
 * nobody signs in to that does not offer RapidSOS's account.
 */
export function orgHoldsCredentials(draft: Pick<IntegrationDraft, 'connectionScope' | 'auth'> & Partial<Pick<IntegrationDraft, 'authModes'>>): boolean {
  return draft.connectionScope !== 'platform' && !authSignsIn(draft.auth) && !(draft.authModes ?? []).includes('platform');
}

/** The auth mode a connection scope is. */
export function scopeMode(scope: IntegrationConnectionScope): IntegrationAuthMode {
  return scope === 'org' ? 'organization' : scope;
}

/**
 * The modes an authentication can work in: a sign-in connects an organization
 * or a person, client credentials sign in as an app, nothing to authenticate
 * runs on RapidSOS's side, and a key, token or login can be anyone's.
 */
export function modesForAuth(auth: Pick<IntegrationDraftAuth, 'kind' | 'grant'>): IntegrationAuthMode[] {
  if (auth.kind === 'none' || auth.kind === 'caller_statement') return ['platform'];
  if (authSignsIn(auth)) return ['organization', 'user'];
  if (auth.kind === 'oauth2') return ['platform', 'organization'];
  return ['platform', 'organization', 'user'];
}

/** The credential keys each authentication kind needs. Fields start with an empty label so nothing looks like a value. */
export const AUTH_CREDENTIAL_KEYS: Record<IntegrationDraftAuth['kind'], string[]> = {
  none: [],
  api_key: ['API_KEY'],
  bearer: ['ACCESS_TOKEN'],
  basic: ['USERNAME', 'PASSWORD'],
  oauth2: ['CLIENT_ID', 'CLIENT_SECRET'],
  caller_statement: ['CALLER_STATEMENT_KEY', 'CALLER_STATEMENT_KID'],
};

const STANDARD_AUTH_KEYS = new Set(Object.values(AUTH_CREDENTIAL_KEYS).flat());

/** Swap the previous auth kind's standard fields for the new one's, keeping any custom field. */
export function credentialFieldsForAuth(
  kind: IntegrationDraftAuth['kind'],
  fields: IntegrationDraftCredentialField[],
): IntegrationDraftCredentialField[] {
  const wanted = new Set(AUTH_CREDENTIAL_KEYS[kind]);
  const kept = fields.filter((field) => !STANDARD_AUTH_KEYS.has(field.key) || wanted.has(field.key));
  const missing = AUTH_CREDENTIAL_KEYS[kind].filter((key) => !kept.some((field) => field.key === key));
  return [...kept, ...missing.map((key) => ({ key, label: '', description: '', exposure: 'server' as const, required: true }))];
}

const UPPERCASE_WORDS = new Set(['API', 'ID', 'URL', 'SID', 'OAUTH', 'JWT', 'SSH', 'PIN']);

/** A readable name for a credential key: `API_KEY` → "API key", `CLIENT_ID` → "Client ID". */
export function labelFromCredentialKey(key: string): string {
  const words = key.split('_').filter(Boolean);
  return words
    .map((word, index) => {
      if (UPPERCASE_WORDS.has(word)) return word === 'OAUTH' ? 'OAuth' : word;
      const lower = word.toLowerCase();
      return index === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
}

/** What customers see as the field's name: the author's label, or one read from the key. */
export function credentialLabel(field: Pick<IntegrationDraftCredentialField, 'key' | 'label'>): string {
  return field.label.trim() || labelFromCredentialKey(field.key) || 'value';
}

/** Placeholder for a secret value box, e.g. "Paste the API key" or "Paste the new access token". */
export function pasteSecretPlaceholder(field: Pick<IntegrationDraftCredentialField, 'key' | 'label'>, replacing = false): string {
  const label = credentialLabel(field);
  const inline = /^[A-Z][a-z]/.test(label) ? label.charAt(0).toLowerCase() + label.slice(1) : label;
  return `Paste the ${replacing ? 'new ' : ''}${inline}`;
}

const GENERATION_KEY = 'studio-admin:integration-generation:';

/** Hand what "Generate with AI" filled to the builder page, which shows it once. */
export function rememberGeneration(draftId: string, generation: IntegrationDraftGeneration): void {
  try {
    window.sessionStorage.setItem(`${GENERATION_KEY}${draftId}`, JSON.stringify(generation));
  } catch {
    return;
  }
}

export function takeGeneration(draftId: string): IntegrationDraftGeneration | undefined {
  try {
    const raw = window.sessionStorage.getItem(`${GENERATION_KEY}${draftId}`);
    if (!raw) return undefined;
    window.sessionStorage.removeItem(`${GENERATION_KEY}${draftId}`);
    return JSON.parse(raw) as IntegrationDraftGeneration;
  } catch {
    return undefined;
  }
}
